import httpService from "../HttpService";
import {
  systemCompanyControllerGetFullCompany,
  systemCompanyControllerGetCompanyUsers,
  systemCompanyControllerGetAll,
  systemCompanyControllerGetStats,
  systemCompanyControllerDeleteCompanyOwner,
  systemCompanyControllerSaveCompanyOwner,
  systemCompanyControllerUpdateCompanyOwner,
  systemCompanyControllerResetOwnerPassword,
  systemCompanyControllerEnableCompany,
  systemCompanyControllerNameUniqueness,
  systemCompanyControllerDisableCompany,
  systemCompanyControllerDeleteCompany,
  systemCompanyControllerUpdateSubscriptionPlan,
  systemCompanyControllerUpdateSettings,
  systemCompanyControllerUpdateBranding,
  systemCompanyControllerRemoveBrandingLogo,
  systemCompanyControllerRemoveBrandingBackground,
  systemCompanyControllerRemoveBrandingImage,
  systemCompanyControllerRemoveWeekImage,
} from "../../api/generated";

// Platform-level company management — SYSTEM_ADMIN only. Backed by the server's
// SystemCompanyController (/system/company/*): create/list/delete companies, company
// owners, enable/disable, subscription plans, and viewing any company by id.
//
// A company ADMIN managing THEIR OWN company (branding, self settings) uses
// ../CompanyService.js (/admin/company/*) instead — see AdminCompanyController.
//
// Migrated to the OpenAPI-generated client. Auth (Bearer) + X-Timezone are injected by
// configureApi's request interceptor, so the per-call token reads are gone. Each function
// preserves its old return shape: these endpoints return their DTOs UN-enveloped (not the
// HttpResponse {statusCode,errorMessage,body} wrapper), so `data` == the old axios
// `response.data`, and callers keep reading `res.data`.
//
// EXCEPTIONS (still on httpService, flagged): createCompany + uploadImage are multipart
// FormData uploads — see TODO(openapi-migration) at each.

// Full company by id (metadata + owners + statistics) — super-admin drill-in view.
export async function getCompany(id) {
  const {data, error} = await systemCompanyControllerGetFullCompany({path: {id}});
  if (error) throw error;
  return {data};
}

// Users of a company holding a given role — e.g. role="ADMIN" or "TRAINER" for the dashboard
// impersonation drill-down (Admins / Trainers tabs).
export async function getCompanyUsersByRole(companyId, role) {
  const {data, error} = await systemCompanyControllerGetCompanyUsers({
    path: {id: companyId},
    query: {withRole: role},
  });
  if (error) throw error;
  return {data};
}

// The cross-company list (every company). Used by super-admin pickers/overviews. Old code
// unwrapped the page's `.content` into `.data`, so callers read the array off `res.data`.
export async function getCompanies(keyword, enabled) {
  const query = {size: 10000};
  if (keyword) query.keyword = keyword;
  if (enabled !== null && enabled !== undefined) query.enabled = enabled;
  const {data, error} = await systemCompanyControllerGetAll({query});
  if (error) throw error;
  return {data: data?.content};
}

export async function getCompaniesPaged(page, size, keyword, enabled, overLimit) {
  const query = {page, size};
  if (keyword) query.keyword = keyword;
  if (enabled !== null && enabled !== undefined) query.enabled = enabled;
  if (overLimit) query.overLimit = true;
  const {data, error} = await systemCompanyControllerGetAll({query});
  if (error) throw error;
  return {data};
}

export async function getCompanyStats() {
  const {data, error} = await systemCompanyControllerGetStats();
  if (error) throw error;
  return {data};
}

export async function deleteOwner(id) {
  const {data, error} = await systemCompanyControllerDeleteCompanyOwner({path: {ownerId: id}});
  if (error) throw error;
  return {data};
}

export async function addOwner(data) {
  const {data: resData, error} = await systemCompanyControllerSaveCompanyOwner({body: data});
  if (error) throw error;
  return {data: resData};
}

export async function editOwner(data) {
  const {data: resData, error} = await systemCompanyControllerUpdateCompanyOwner({body: data});
  if (error) throw error;
  return {data: resData};
}

export async function resetOwnerPassword(ownerId, newPassword) {
  const {data, error} = await systemCompanyControllerResetOwnerPassword({
    path: {ownerId},
    body: {newPassword},
  });
  if (error) throw error;
  return {data};
}

// Create a brand-new company (SYSTEM_ADMIN only). The company-admin self-save is the
// separate saveCompany() in ../CompanyService.js (POST /admin/company, own company only).
// TODO(openapi-migration): multipart/FormData upload (metaData string + optional image
// blob) — kept on httpService per the migration policy (multipart uploads stay on raw
// transport). The generated SaveCompany op models a {file, metaData} body but we leave this
// on the proven FormData path.
export async function createCompany(metaData, file) {
  const token = localStorage.getItem("auth");
  const formData = new FormData();

  if (file) {
    // @type {any} — a fetched Blob has no `.name` at the type level; this is the
    // pre-existing (flagged) multipart path, so we preserve the runtime behavior exactly.
    /** @type {any} */
    const blob = await fetch(file).then(r => r.blob());
    formData.append("file", blob, blob.name);
  }

  formData.append("metaData", metaData);

  return await httpService.post(`system/company`, formData, token);
}

export const enableCompany = async (id) => {
  const {data, error} = await systemCompanyControllerEnableCompany({path: {id}});
  if (error) throw error;
  return {data};
};

export const isCompanyNameValid = async (name) => {
  const {data, error} = await systemCompanyControllerNameUniqueness({path: {name}});
  if (error) throw error;
  return {data};
};

export const disableComapny = async (id) => {
  const {data, error} = await systemCompanyControllerDisableCompany({path: {id}});
  if (error) throw error;
  return {data};
};

export const deleteCompany = async (id) => {
  const {data, error} = await systemCompanyControllerDeleteCompany({path: {companyId: id}});
  if (error) throw error;
  return {data};
};

// TODO(openapi-migration): multipart/FormData image upload — kept on httpService per the
// migration policy (multipart uploads stay on raw transport).
export const uploadImage = async (id, file) => {
  const token = localStorage.getItem("auth");
  const formData = new FormData();
  formData.append("file", file, file.name);
  return await httpService.post(`system/company/updateImage/${id}`, formData, token);
};

// Settings toggles for ANY company, by id. A super-admin cannot use ../CompanyService.js's
// saveCompany for this: that endpoint takes the target company from the caller's principal, so a
// company-less SYSTEM_ADMIN gets a 403 (and an impersonating one would write to the wrong company).
//
// This is a PATCH, not a blind overwrite — omitted fields are left untouched server-side — so pass
// only the toggle that changed rather than resending the whole settings whitelist.
export const updateCompanySettings = async (id, settings) => {
  const {data, error} = await systemCompanyControllerUpdateSettings({
    path: {id},
    body: settings,
  });
  if (error) throw error;
  return {data};
};

// Identity + branding + white-label binding for ANY company, by id. Same reasoning as
// updateCompanySettings. Images (logo/background/week slots) remain separate multipart endpoints.
//
// Partial-update semantics, with one wrinkle on the white-label fields: omitting a field keeps its
// current value, while sending an empty string CLEARS it (un-binds the brand).
export const updateCompanyBranding = async (id, branding) => {
  const {data, error} = await systemCompanyControllerUpdateBranding({
    path: {id},
    body: branding,
  });
  if (error) throw error;
  return {data};
};

// Branding IMAGES for ANY company, by id — the by-id twins of ../CompanyService.js's
// uploadBrandingLogo / removeBrandingLogo / ... family.
//
// A super-admin cannot use those: they are /admin/company routes, which are ADMIN-only (a
// SYSTEM_ADMIN is not on the /admin floor → 403) and force the target to the CALLER's own company,
// which a company-less super-admin doesn't have. Before these existed, a super-admin editing a
// company's branding saved the text fields (already a /system route) and then 403'd on the very
// first image call — a half-applied save that surfaced as "update failed".
//
// TODO(openapi-migration): the three uploads are multipart/FormData — kept on httpService per the
// migration policy. The removals go through the generated client.
export const uploadBrandingLogo = async (id, file) => {
  const token = localStorage.getItem("auth");
  const formData = new FormData();
  formData.append("file", file, file.name);
  return await httpService.post(`system/company/${id}/brandingLogo`, formData, token);
};

export const removeBrandingLogo = async (id) => {
  const {data, error} = await systemCompanyControllerRemoveBrandingLogo({path: {id}});
  if (error) throw error;
  return {data};
};

export const uploadBrandingBackground = async (id, file) => {
  const token = localStorage.getItem("auth");
  const formData = new FormData();
  formData.append("file", file, file.name);
  return await httpService.post(`system/company/${id}/brandingBackground`, formData, token);
};

export const removeBrandingBackground = async (id) => {
  const {data, error} = await systemCompanyControllerRemoveBrandingBackground({path: {id}});
  if (error) throw error;
  return {data};
};

export const uploadBrandingImage = async (id, imageType, imageKey, file) => {
  const token = localStorage.getItem("auth");
  const formData = new FormData();
  formData.append("file", file, file.name);
  return await httpService.post(
      `system/company/${id}/brandingImage?imageType=${imageType}&imageKey=${encodeURIComponent(imageKey)}`,
      formData,
      token);
};

export const removeBrandingImage = async (id, imageType, imageKey) => {
  const {data, error} = await systemCompanyControllerRemoveBrandingImage({
    path: {id},
    query: {imageType, imageKey},
  });
  if (error) throw error;
  return {data};
};

// Removing a week image reindexes the remaining ones server-side, so the slots stay 0..n-1.
export const removeWeekImage = async (id, imageKey) => {
  const {data, error} = await systemCompanyControllerRemoveWeekImage({path: {id, imageKey}});
  if (error) throw error;
  return {data};
};

export const updateSubscriptionPlan = async (id, subscriptionPlan) => {
  const {data, error} = await systemCompanyControllerUpdateSubscriptionPlan({
    path: {id},
    query: {subscriptionPlan},
  });
  if (error) throw error;
  return {data};
};
